/**
 * Celkový ceník — hash-route #info-cenik.
 *
 * Ceny odpovídají tomu, co skutečně účtuje rezervační systém:
 *  - chatka/tiny house: CHATKA_PRICING / TINYHOUSE_PRICING (sezóna × víkend)
 *  - bivak 12h slot, karavan noc, sport hodina: DEFAULT_PRICES
 *  - povolenky: ceník na místě (sekce Rybolov)
 * Při změně cen v backendu je nutné upravit i tuto stránku.
 */
import { useWebPrices, czk } from '../lib/webPrices'

type Row = { label: string; note?: string; season: string; off: string }

const UBYTOVANI: Row[] = [
  {
    label: 'Rybářská chatka',
    note: 'všední noc (ne–čt)',
    season: '850 Kč',
    off: '450 Kč',
  },
  {
    label: 'Rybářská chatka',
    note: 'víkend (pá, so)',
    season: '1 190 Kč',
    off: '790 Kč',
  },
  {
    label: 'Tiny house',
    note: 'všední noc, minimálně 2 noci',
    season: '2 390 Kč',
    off: '1 800 Kč',
  },
  {
    label: 'Tiny house',
    note: 'víkend (pá, so), minimálně 2 noci',
    season: '2 990 Kč',
    off: '1 950 Kč',
  },
  {
    label: 'Karavan — stání',
    note: 'za 24 h, 4 stání',
    season: '290 Kč',
    off: '290 Kč',
  },
  {
    label: 'Bivak',
    note: '12h slot (7:00–19:00 nebo 19:00–7:00), 7 míst',
    season: '100 Kč',
    off: '100 Kč',
  },
]

const POVOLENKY: { label: string; price: string }[] = [
  { label: '6 hodin', price: '300 Kč' },
  { label: '12 hodin', price: '490 Kč' },
  { label: '24 hodin', price: '690 Kč' },
  { label: '2 dny', price: '1 190 Kč' },
  { label: '3 dny', price: '1 790 Kč' },
  { label: '4 dny', price: '2 290 Kč' },
  { label: '5 dní', price: '2 590 Kč' },
  { label: '6 dní', price: '2 990 Kč' },
  { label: '7 dní', price: '3 390 Kč' },
]

const PRIPLATKY: { label: string; price: string; note?: string }[] = [
  { label: 'Elektrická přípojka — karavan', price: '200 Kč', note: 'za 24 h' },
  { label: 'Elektrická přípojka — bivak', price: '100 Kč', note: 'za 12h slot' },
  { label: 'Podběrák', price: '200 Kč', note: 'za 24 h' },
  { label: 'Vanička', price: '200 Kč', note: 'za 24 h' },
  { label: 'Pouštěcí vak', price: '100 Kč', note: 'za 24 h' },
  { label: 'Míč k zapůjčení — beach volejbal', price: '50 Kč' },
]

function Table({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full text-[15px] text-ink-800">{children}</table>
    </div>
  )
}

export function Cenik() {
  const prices = useWebPrices()
  const tenis = prices.tenis_h ? `${czk(prices.tenis_h)} Kč` : '120 Kč'
  const beach = prices.beach_h ? `${czk(prices.beach_h)} Kč` : '120 Kč'

  return (
    <section className="bg-paper py-12 md:py-20 min-h-screen">
      <div className="container-x max-w-3xl">
        <div className="text-[10px] uppercase tracking-[0.30em] text-brand-500 font-bold flex items-center gap-2">
          <span className="inline-block w-8 h-px bg-brand-500" />
          Sobáčov · Fishing &amp; Sport Resort
        </div>
        <h1 className="h-display text-4xl md:text-5xl mt-3">Celkový ceník</h1>
        <p className="text-ink-700 mt-3 text-sm">
          Ceny jsou uvedeny včetně DPH. <b className="text-ink-900">Sezóna</b> je od 2. dubna do
          30. září, <b className="text-ink-900">mimo sezónu</b> od 1. října do 1. dubna.
        </p>

        {/* Ubytování */}
        <div className="mt-10 card p-6 md:p-7">
          <h2 className="font-bold text-lg text-ink-900 tracking-tight">Ubytování</h2>
          <p className="mt-1 text-[13px] text-ink-700">
            Ceny za jednotku a noc, pokud není uvedeno jinak. Víkendem se rozumí noc z pátku
            a ze soboty.
          </p>
          <Table>
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.12em] text-ink-700/60 font-semibold border-b border-ink-900/10">
                <th className="text-left py-2 pr-3">Ubytování</th>
                <th className="text-right py-2 pr-3 whitespace-nowrap">Sezóna<br />2. 4. – 30. 9.</th>
                <th className="text-right py-2 whitespace-nowrap">Mimo sezónu<br />1. 10. – 1. 4.</th>
              </tr>
            </thead>
            <tbody>
              {UBYTOVANI.map((r, i) => (
                <tr key={i} className="border-b border-ink-900/[0.06] align-top">
                  <td className="py-2.5 pr-3">
                    <div className="font-semibold text-ink-900">{r.label}</div>
                    {r.note && <div className="text-[12.5px] text-ink-700">{r.note}</div>}
                  </td>
                  <td className="py-2.5 pr-3 text-right font-bold tabular-nums whitespace-nowrap">{r.season}</td>
                  <td className="py-2.5 text-right font-bold tabular-nums whitespace-nowrap">{r.off}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <p className="mt-3 text-[13px] text-ink-700">
            <b className="text-ink-900">Pokoje penzionu</b> se rezervují na Booking.com, kde
            najdete i aktuální ceny podle termínu a obsazenosti.
          </p>
        </div>

        {/* Rybolov */}
        <div className="mt-8 card p-6 md:p-7">
          <h2 className="font-bold text-lg text-ink-900 tracking-tight">Povolenky k rybolovu</h2>
          <p className="mt-1 text-[13px] text-ink-700">
            Povolenku <b className="text-ink-900">nelze rezervovat předem</b> — vystavíme ji na
            místě, uhradíte hotově nebo kartou. Ceny jsou stejné po celý rok.
          </p>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-x-5 gap-y-1.5 text-[15px]">
            {POVOLENKY.map((p) => (
              <div key={p.label} className="flex items-baseline justify-between border-b border-ink-900/[0.08] pb-1">
                <span className="text-ink-700">{p.label}</span>
                <span className="font-bold text-ink-900 tabular-nums">{p.price}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Sport */}
        <div className="mt-8 card p-6 md:p-7">
          <h2 className="font-bold text-lg text-ink-900 tracking-tight">Sport</h2>
          <p className="mt-1 text-[13px] text-ink-700">
            Hodinové sloty od 9:00 do 21:00, rezervace online. Ceny platí celoročně.
          </p>
          <Table>
            <tbody>
              <tr className="border-b border-ink-900/[0.06]">
                <td className="py-2.5 pr-3">
                  <div className="font-semibold text-ink-900">Tenisový kurt</div>
                  <div className="text-[12.5px] text-ink-700">3 kurty, syntetický povrch · za hodinu</div>
                </td>
                <td className="py-2.5 text-right font-bold tabular-nums whitespace-nowrap">{tenis}</td>
              </tr>
              <tr className="border-b border-ink-900/[0.06]">
                <td className="py-2.5 pr-3">
                  <div className="font-semibold text-ink-900">Beach volejbal</div>
                  <div className="text-[12.5px] text-ink-700">3 hřiště u rybníka · za hodinu</div>
                </td>
                <td className="py-2.5 text-right font-bold tabular-nums whitespace-nowrap">{beach}</td>
              </tr>
            </tbody>
          </Table>
        </div>

        {/* Příplatky */}
        <div className="mt-8 card p-6 md:p-7">
          <h2 className="font-bold text-lg text-ink-900 tracking-tight">Příplatky a zapůjčení</h2>
          <Table>
            <tbody>
              {PRIPLATKY.map((p) => (
                <tr key={p.label} className="border-b border-ink-900/[0.06]">
                  <td className="py-2.5 pr-3">
                    <div className="font-semibold text-ink-900">{p.label}</div>
                    {p.note && <div className="text-[12.5px] text-ink-700">{p.note}</div>}
                  </td>
                  <td className="py-2.5 text-right font-bold tabular-nums whitespace-nowrap">{p.price}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        {/* Akce a catering */}
        <div className="mt-8 card p-6 md:p-7">
          <h2 className="font-bold text-lg text-ink-900 tracking-tight">Akce, svatby a catering</h2>
          <Table>
            <tbody>
              <tr className="border-b border-ink-900/[0.06]">
                <td className="py-2.5 pr-3">
                  <div className="font-semibold text-ink-900">Pronájem restaurace</div>
                  <div className="text-[12.5px] text-ink-700">až pro 60 hostů · základní cena</div>
                </td>
                <td className="py-2.5 text-right font-bold tabular-nums whitespace-nowrap">od 8 000 Kč</td>
              </tr>
              <tr className="border-b border-ink-900/[0.06]">
                <td className="py-2.5 pr-3">
                  <div className="font-semibold text-ink-900">Catering na míru</div>
                  <div className="text-[12.5px] text-ink-700">za osobu, podle vybraného menu</div>
                </td>
                <td className="py-2.5 text-right font-bold tabular-nums whitespace-nowrap">od 300 Kč</td>
              </tr>
            </tbody>
          </Table>
          <p className="mt-3 text-[13px] text-ink-700">
            Akce připravujeme individuálně — ozvěte se na{' '}
            <a href="tel:+420775140705" className="font-semibold text-ink-900 hover:text-brand-600">
              +420 775 140 705
            </a>{' '}
            a připravíme nabídku.
          </p>
        </div>

        <p className="mt-6 text-[13px] text-ink-700">
          Storno podmínky a způsob platby najdete v{' '}
          <a href="#podminky" className="font-semibold text-ink-900 hover:text-brand-600">
            Obchodních podmínkách
          </a>
          . Ceny se mohou měnit; platí ceník zobrazený v okamžiku rezervace.
        </p>

        <a href="#top" onClick={() => { window.location.hash = '' }} className="btn-outline-dark mt-8">
          Zpět na hlavní stránku
        </a>
      </div>
    </section>
  )
}
